import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Typography,
    Box,
    Breadcrumbs,
    Link,
    CircularProgress,
    Alert
} from '@mui/material';
import axios from 'axios';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import GitHubIcon from '@mui/icons-material/GitHub';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const GitHubImportDialog = ({ open, onClose, token, onImportSuccess }) => {
    // states: 'repos', 'files', 'loading'
    const [view, setView] = useState('repos');
    const [repos, setRepos] = useState([]);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedRepo, setSelectedRepo] = useState(null);
    const [currentPath, setCurrentPath] = useState(''); // folder path
    const [pathHistory, setPathHistory] = useState([]); // Array of  { name, path }

    // Fetch Repos on Open
    useEffect(() => {
        if (open) {
            fetchRepos();
        } else {
            // Reset state on close
            setView('repos');
            setRepos([]);
            setFiles([]);
            setSelectedRepo(null);
            setCurrentPath('');
            setPathHistory([]);
            setError(null);
        }
    }, [open]);

    const fetchRepos = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get('/api/integrations/github/repos', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRepos(res.data);
            setView('repos');
        } catch (err) {
            console.error(err);
            setError('Failed to load repositories. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const fetchFiles = async (repo, path = '') => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get('/api/integrations/github/files', {
                headers: { Authorization: `Bearer ${token}` },
                params: { owner: repo.owner.login || repo.full_name.split('/')[0], repo: repo.name, path }
            });
            // Sort: folders first
            const sorted = res.data.sort((a, b) => {
                if (a.type === b.type) return a.name.localeCompare(b.name);
                return a.type === 'dir' ? -1 : 1;
            });
            setFiles(sorted);
            setView('files');
            // Update selection state
            if (!selectedRepo) setSelectedRepo(repo);
            setCurrentPath(path);
        } catch (err) {
            console.error(err);
            setError('Failed to load files.');
        } finally {
            setLoading(false);
        }
    };

    const handleRepoClick = (repo) => {
        fetchFiles(repo, '');
    };

    const handleFileClick = (file) => {
        if (file.type === 'dir') {
            // Push current path to history or just rely on file.path
            // file.path is the full path from root
            fetchFiles(selectedRepo, file.path);
        } else {
            // It's a file, trigger import
            handleImport(file);
        }
    };

    const handleBackToRepos = () => {
        setView('repos');
        setSelectedRepo(null);
        setCurrentPath('');
    };

    const handleBreadcrumbClick = (path) => {
        fetchFiles(selectedRepo, path);
    };

    const handleImport = async (file) => {
        if (!window.confirm(`Import ${file.name}?`)) return;

        setLoading(true);
        try {
            const owner = selectedRepo.full_name.split('/')[0];
            const res = await axios.post('/api/integrations/github/import', {
                owner,
                repo: selectedRepo.name,
                filePath: file.path,
                downloadUrl: file.download_url
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // notify parent
            if (onImportSuccess) onImportSuccess(res.data.projectId);
            onClose();

        } catch (err) {
            console.error(err);
            setError('Import failed. ' + (err.response?.data?.msg || err.message));
        } finally {
            setLoading(false);
        }
    };

    // Breadcrumb construction
    const renderBreadcrumbs = () => {
        // Simple Logic: Root > [Repo Name] > ...folders
        if (!selectedRepo) return null;

        const parts = currentPath ? currentPath.split('/').filter(p => p) : [];

        return (
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
                <Link underline="hover" color="inherit" onClick={handleBackToRepos} style={{ cursor: 'pointer' }}>
                    Repositories
                </Link>
                <Typography color="text.primary" onClick={() => fetchFiles(selectedRepo, '')} style={{ cursor: 'pointer' }}>
                    {selectedRepo.name}
                </Typography>
                {parts.map((part, index) => {
                    const path = parts.slice(0, index + 1).join('/');
                    const isLast = index === parts.length - 1;
                    return isLast ? (
                        <Typography key={path} color="text.primary">{part}</Typography>
                    ) : (
                        <Link
                            key={path}
                            underline="hover"
                            color="inherit"
                            onClick={() => handleBreadcrumbClick(path)}
                            style={{ cursor: 'pointer' }}
                        >
                            {part}
                        </Link>
                    );
                })}
            </Breadcrumbs>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GitHubIcon /> Import from GitHub
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: 400 }}>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {view === 'files' ? renderBreadcrumbs() : null}

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List>
                        {view === 'repos' && !loading && (
                            repos.map(repo => (
                                <ListItem button key={repo.id} onClick={() => handleRepoClick(repo)}>
                                    <ListItemIcon>
                                        <FolderIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={repo.name}
                                        secondary={repo.private ? 'Private' : 'Public'}
                                    />
                                </ListItem>
                            ))
                        )}
                        {view === 'repos' && repos.length === 0 && !loading && (
                            <Typography sx={{ p: 2, color: 'text.secondary' }}>No repositories found.</Typography>
                        )}

                        {view === 'files' && !loading && (
                            files.map(file => (
                                <ListItem button key={file.path} onClick={() => handleFileClick(file)}>
                                    <ListItemIcon>
                                        {file.type === 'dir' ? <FolderIcon color="action" /> : <InsertDriveFileIcon />}
                                    </ListItemIcon>
                                    <ListItemText primary={file.name} />
                                </ListItem>
                            ))
                        )}

                        {view === 'files' && files.length === 0 && !loading && (
                            <Typography sx={{ p: 2, color: 'text.secondary' }}>No files found.</Typography>
                        )}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default GitHubImportDialog;
